// Silnik konfiguratora łóżka — stan, cena, walidacja, dodanie do koszyka.
// Kontrakt DOM + JSON: docs/CONFIGURATOR-SPEC.md. Zero zależności zewnętrznych.
// Ceny liczone wyłącznie do podglądu — źródłem prawdy w checkout są warianty i produkty-dopłaty.

if (!customElements.get('bed-configurator')) {
  customElements.define(
    'bed-configurator',
    class BedConfigurator extends HTMLElement {
      constructor() {
        super();

        // Defensywnie: brak lub uszkodzony JSON → element pozostaje bierny (działa fallback HTML)
        const dataEl = this.querySelector('script[type="application/json"][data-bed-config]');
        if (!dataEl) return;
        try {
          this.config = JSON.parse(dataEl.textContent);
        } catch (error) {
          this.config = null;
        }
        if (!this.config || !Array.isArray(this.config.variants) || !Array.isArray(this.config.groups)) return;
        if (!this.config.variants.length) return;

        // Teksty z locales przekazane przez Liquid w config.strings (opcjonalne, z fallbackami PL)
        this.strings = this.config.strings || {};
        this.dimCount = Math.min(2, (this.config.variants[0].options || []).length || 2);
        this.atcEl = this.querySelector('[data-bed-atc]');
        // Liquid renderuje przycisk z natywnym `disabled` (ochrona przed klikiem przed hydratacją).
        // Po inicjalizacji sterujemy wyłącznie aria-disabled, żeby klik pokazywał braki walidacji.
        if (this.atcEl) this.atcEl.removeAttribute('disabled');
        this.totalEl = this.querySelector('[data-bed-total]');
        this.stickyEl = this.querySelector('.bed-sticky');
        this.busy = false;

        // Stan: wymiary (osie wariantu) + wybory w grupach
        this.state = { dims: this.initDims(), picks: this.initPicks() };
        this.restoreConfiguration();
        // Baza do etykiet dopłat wymiarów: zawsze defaulty (niezależnie od stanu :checked,
        // który przeglądarka mogła przywrócić po „wstecz") — formuła ze spec liczy od defaultA
        this.baseDims = this.initBaseDims();

        this.syncDimInputs();
        this.syncPickInputs();
        this.renderAddonPriceLabels();
        this.updateDimensionUI(false);
        this.updateDelivery();
        this.updateTotal();
        this.updateSummary();
        this.updateDiagrams();
        this.updateMainImage();
        this.updateAtcState();

        this.addEventListener('change', this.onChange.bind(this));
        const reset = this.querySelector('[data-bed-reset]');
        if (reset) {
          reset.hidden = false;
          reset.addEventListener('click', () => this.resetConfiguration());
        }
        this.bindShareButtons();
        if (this.atcEl) this.atcEl.addEventListener('click', this.onAtcClick.bind(this));
      }

      track(event, data) {
        // Analytics is optional: consent/provider failures must never interrupt configuration or checkout.
        try {
          const privacy = window.Shopify?.customerPrivacy;
          if (privacy?.currentVisitorConsent?.()?.analytics !== 'yes' || !privacy?.analyticsProcessingAllowed?.()) return;
          Promise.resolve(window.Shopify?.analytics?.publish?.(`klove:${event}`, data)).catch(() => {});
        } catch {}
      }

      /* ---------- inicjalizacja stanu ---------- */

      // Wymiary: zaznaczony input → data-bed-default → pierwszy dostępny wariant
      initDims() {
        const dims = [];
        for (let axis = 0; axis < this.dimCount; axis++) {
          const fieldset = this.groupFieldset(`dim:${axis}`);
          let value = null;
          if (fieldset) {
            const input =
              fieldset.querySelector('input[type="radio"]:checked') ||
              fieldset.querySelector('input[type="radio"][data-bed-default]');
            if (input) value = input.value;
          }
          dims.push(value);
        }
        if (dims.some((value) => value == null)) {
          const variant = this.config.variants.find((v) => v.available) || this.config.variants[0];
          if (variant && Array.isArray(variant.options)) {
            for (let axis = 0; axis < this.dimCount; axis++) {
              if (dims[axis] == null && variant.options[axis] != null) dims[axis] = variant.options[axis];
            }
          }
        }
        return dims;
      }

      // Baza etykiet dopłat: wyłącznie defaulty (data-bed-default → pierwszy dostępny wariant),
      // bez patrzenia na :checked — restore formularza przez przeglądarkę nie może zmienić bazy
      initBaseDims() {
        const variant = this.config.variants.find(item => item.available) || this.config.variants[0];
        return variant ? variant.options.slice(0, this.dimCount) : this.state.dims.slice();
      }

      initPicks() {
        const picks = {};
        this.config.groups.forEach(group => {
          const fieldset = this.groupFieldset(group.key);
          const input = fieldset?.querySelector('input[type="radio"]:checked:not([disabled])') || fieldset?.querySelector('input[type="radio"][data-bed-default]:not([disabled])');
          picks[group.key] = input?.value || null;
        });
        return picks;
      }

      storageKey() { return `klove:configuration:v1:${this.config.productId}`; }

      restoreConfiguration() {
        let saved = null;
        try { const data = JSON.parse(window.sessionStorage.getItem(this.storageKey())); if (data?.version === 1) saved = data; } catch {}
        const params = new URLSearchParams(window.location.search);
        const shared = params.get('configuration') === '1';
        let restored = false;
        let linked = false;
        let stale = false;
        const requestedVariant = params.get('variant');
        if (requestedVariant !== null) {
          const variant = this.config.variants.find(item => String(item.id) === requestedVariant && item.available);
          if (variant) { this.state.dims = variant.options.slice(0, this.dimCount); linked = true; } else stale = true;
        } else if (!shared && saved?.dims) {
          const variant = Array.isArray(saved.dims) && this.findVariant(saved.dims);
          if (variant?.available) { this.state.dims = variant.options.slice(0, this.dimCount); restored = true; } else stale = true;
        }
        this.refreshDimensionAddons();
        this.config.groups.forEach(group => {
          const name = `bed_option_${group.key}`;
          const legacy = group.key === 'headboard' ? 'model' : group.key;
          const explicit = params.has(name) ? params.get(name) : shared ? null : params.get(legacy);
          const usable = value => (group.options || []).some(option => option.value === value && option.available !== false && (!option.addonPriceCents || option.addonVariantId));
          if (explicit !== null) {
            if (usable(explicit)) { this.state.picks[group.key] = explicit; linked = true; } else stale = true;
          } else if (!shared && saved?.picks?.[group.key] != null) {
            if (usable(saved.picks[group.key])) { this.state.picks[group.key] = saved.picks[group.key]; restored = true; } else stale = true;
          }
        });
        for (const name of params.keys()) {
          if (name.startsWith('bed_option_') && !this.config.groups.some(group => name === `bed_option_${group.key}`)) stale = true;
        }
        const notice = this.querySelector('[data-bed-saved-status]');
        if (notice) notice.textContent = stale ? this.getString('configuration_stale', '') : linked ? this.getString('configuration_link_restored', '') : restored ? this.getString('configuration_restored', '') : '';
      }

      configurationUrl() {
        this.refreshDimensionAddons();
        const variant = this.findVariant(this.state.dims);
        if (!variant?.available || !this.config.productUrl) return null;
        const url = new URL(this.config.productUrl, window.location.origin);
        if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
        url.search = ''; url.hash = 'konfigurator';
        url.searchParams.set('configuration', '1');
        url.searchParams.set('variant', String(variant.id));
        for (const group of this.config.groups) {
          const pick = this.state.picks[group.key];
          const option = (group.options || []).find(item => item.value === pick && item.available !== false && (!item.addonPriceCents || item.addonVariantId));
          if ((pick != null || group.required) && !option) return null;
          if (option) url.searchParams.set(`bed_option_${group.key}`, option.value);
        }
        return url.href;
      }

      bindShareButtons() {
        this._shareButtons ||= new WeakSet();
        this.querySelectorAll('[data-bed-share]').forEach(button => {
          button.hidden = false;
          if (this._shareButtons.has(button)) return;
          this._shareButtons.add(button);
          button.addEventListener('click', () => this.shareConfiguration());
        });
      }

      async shareConfiguration() {
        if (this.busy) return;
        const output = this.querySelector('[data-bed-share-output]');
        const input = this.querySelector('[data-bed-share-url]');
        const status = this.querySelector('[data-bed-share-status]');
        let url;
        try { url = this.configurationUrl(); } catch {}
        if (output) output.hidden = false;
        if (input) input.value = '';
        if (status) status.textContent = '';
        if (!url) { if (status) status.textContent = this.getString('share_unavailable', ''); return; }
        if (input) input.value = url;
        try {
          if (!window.navigator?.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await window.navigator.clipboard.writeText(url);
          if (status) status.textContent = this.getString('share_copied', '');
        } catch {
          if (status) status.textContent = this.getString('share_manual', '');
          input?.focus(); input?.select();
        }
      }

      saveConfiguration() {
        try {
          window.sessionStorage.setItem(this.storageKey(), JSON.stringify({version:1, dims:this.state.dims, picks:this.state.picks}));
        } catch {}
      }

      syncPickInputs() {
        this.config.groups.forEach(group => {
          this.groupFieldset(group.key)?.querySelectorAll('input[type="radio"]').forEach(input => {
            input.checked = input.value === this.state.picks[group.key];
          });
        });
      }

      resetConfiguration() {
        if (this.busy) return;
        this.state.dims = this.initBaseDims();
        this.refreshDimensionAddons();
        this.config.groups.forEach(group => {
          const input = this.groupFieldset(group.key)?.querySelector('input[type="radio"][data-bed-default]:not([disabled])');
          this.state.picks[group.key] = input?.value || null;
        });
        this.syncDimInputs(); this.syncPickInputs(); this.hideStickyError();
        this.querySelectorAll('[data-bed-group]').forEach(fieldset => this.clearGroupError(fieldset));
        this.updateDimensionUI(false); this.updateTotal(); this.updateSummary();
        this.updateDiagrams(); this.updateMainImage(); this.updateAtcState();
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('configuration'); url.searchParams.delete('variant');
          [...url.searchParams.keys()].filter(key => key.startsWith('bed_option_')).forEach(key => url.searchParams.delete(key));
          this.config.groups.forEach(group => { url.searchParams.delete(`bed_option_${group.key}`); url.searchParams.delete(group.key === 'headboard' ? 'model' : group.key); });
          window.history.replaceState(window.history.state, '', url.href);
        } catch {}
        this.saveConfiguration();
        const resetNotice = this.querySelector('[data-bed-notice]'); if (resetNotice) resetNotice.textContent = '';
        const shareOutput = this.querySelector('[data-bed-share-output]'); if (shareOutput) shareOutput.hidden = true;
        const notice = this.querySelector('[data-bed-saved-status]');
        if (notice) notice.textContent = this.getString('configuration_reset', '');
        this.dispatchEvent(new Event('change', {bubbles:true}));
      }

      // Zaznacz radia wymiarów zgodnie ze stanem (gdy default przyszedł z wariantu, nie z DOM)
      syncDimInputs() {
        for (let axis = 0; axis < this.dimCount; axis++) {
          const fieldset = this.groupFieldset(`dim:${axis}`);
          if (!fieldset || this.state.dims[axis] == null) continue;
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = input.value === this.state.dims[axis];
          });
        }
      }

      /* ---------- pomocnicze ---------- */

      groupFieldset(key) {
        // Compare data values directly; merchant group keys never become selector source.
        return [...this.querySelectorAll('[data-bed-group]')].find(fieldset => fieldset.dataset.bedGroup === key) || null;
      }

      getString(key, fallback) {
        const value = this.strings[key];
        return typeof value === 'string' && value.length ? value : fallback;
      }

      optionLabelEl(input) {
        return (input.labels && input.labels[0]) || input.closest('label');
      }

      findVariant(dims) {
        return this.config.variants.find((variant) => {
          for (let i = 0; i < this.dimCount; i++) {
            if (variant.options[i] !== dims[i]) return false;
          }
          return true;
        });
      }

      /* ---------- pieniądze ---------- */

      // Format przez shop.money_format — podmiana placeholdera {{ amount* }}
      formatMoney(cents) {
        const format = String(this.config.moneyFormat || '{{ amount_with_comma_separator }}').replace(/<[^>]*>/g, '');
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const key = match ? match[1] : 'amount';
        let value;
        switch (key) {
          case 'amount_no_decimals':
            value = this.formatDelimited(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = this.formatDelimited(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = this.formatDelimited(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = this.formatDelimited(cents, 2, "'", '.');
            break;
          case 'amount_with_space_separator':
            value = this.formatDelimited(cents, 2, ' ', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = this.formatDelimited(cents, 0, ' ', ',');
            break;
          default:
            value = this.formatDelimited(cents, 2, ',', '.');
        }
        return match ? format.replace(match[0], value) : value;
      }

      formatDelimited(cents, precision, thousands, decimal) {
        const negative = cents < 0;
        const fixed = (Math.abs(cents) / 100).toFixed(precision);
        const parts = fixed.split('.');
        const grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return (negative ? '-' : '') + grouped + (parts[1] ? decimal + parts[1] : '');
      }

      // Etykieta dopłaty: 0 → „0 zł", dodatnia → „+X zł", ujemna → „−X zł"
      surchargeLabel(cents) {
        if (!cents) return this.getString('included', '');
        const money = this.formatMoney(Math.abs(cents));
        if (cents < 0) return '−' + money;
        return this.getString('surcharge', '+[amount]')
          .replace('[amount]', money)
          .replace(/\{\{\s*amount\s*\}\}/, money);
      }

      /* ---------- rendering ---------- */

      resolveDimensionAddon(option) {
        const mapping = option.dimensionMap;
        const variants = option.addonVariants;
        if (!Array.isArray(mapping) || mapping.length !== this.dimCount || !Array.isArray(variants)) return null;
        const bedAxes = new Set(), addonAxes = new Set();
        for (const pair of mapping) {
          if (!pair || !Number.isInteger(pair.bedAxis) || !Number.isInteger(pair.addonAxis) || pair.bedAxis < 0 || pair.bedAxis >= this.dimCount || pair.addonAxis < 0 || pair.addonAxis >= mapping.length || bedAxes.has(pair.bedAxis) || addonAxes.has(pair.addonAxis)) return null;
          bedAxes.add(pair.bedAxis); addonAxes.add(pair.addonAxis);
        }
        const matches = variants.filter(variant => Array.isArray(variant.options) && variant.options.length === mapping.length && mapping.every(pair => typeof variant.options[pair.addonAxis] === 'string' && typeof this.state.dims[pair.bedAxis] === 'string' && variant.options[pair.addonAxis].trim() === this.state.dims[pair.bedAxis].trim()));
        if (matches.length !== 1) return null;
        const variant = matches[0];
        return variant.id && Number.isInteger(variant.priceCents) && variant.priceCents >= 0 ? variant : null;
      }

      refreshDimensionAddons() {
        this.config.groups.forEach(group => (group.options || []).forEach(option => {
          if (!option.dimensionMatched) return;
          const variant = this.resolveDimensionAddon(option);
          option.available = !!variant && variant.available === true;
          option.addonVariantId = option.available ? variant.id : null;
          option.addonPriceCents = option.available ? variant.priceCents : 0;
          option.addonCompareAtPriceCents = option.available ? (variant.compareAtPriceCents || variant.priceCents) : 0;
        }));
      }

      // Statyczne etykiety dopłat dla grup opcji (wezgłowie, tkanina, materac, strona)
      renderAddonPriceLabels() {
        this.refreshDimensionAddons();
        this.config.groups.forEach((group) => {
          const fieldset = this.groupFieldset(group.key);
          if (!fieldset) return;
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const option = (group.options || []).find((o) => o.value === input.value);
            const label = this.optionLabelEl(input);
            const priceEl = label && label.querySelector('.bed-option__price');
            if (option?.dimensionMatched) {
              input.disabled = option.available === false;
              label?.classList.toggle('bed-option--disabled', input.disabled);
            }
            if (priceEl) priceEl.textContent = option?.available === false ? this.getString('unavailable', '') : this.surchargeLabel((option && option.addonPriceCents) || 0);
          });
        });
      }

      dimensionVariant(axis, value) {
        const desired = this.state.dims.slice();
        desired[axis] = value;
        const exact = this.findVariant(desired);
        if (exact?.available) return exact;
        // Keep as many other axes as possible; stable merchant variant order breaks ties.
        return this.config.variants.filter(variant => variant.available && variant.options[axis] === value)
          .reduce((best, variant) => {
            const matches = candidate => candidate.options.slice(0, this.dimCount).filter((option, index) => index !== axis && option === desired[index]).length;
            return !best || matches(variant) > matches(best) ? variant : best;
          }, null);
      }

      selectDimension(axis, value) {
        const variant = this.dimensionVariant(axis, value);
        if (!variant) { this.syncDimInputs(); return false; }
        const adjusted = this.state.dims.some((dimension, index) => index !== axis && dimension !== variant.options[index]);
        this.state.dims = variant.options.slice(0, this.dimCount);
        this.syncDimInputs();
        const notice = this.querySelector('[data-bed-notice]');
        if (notice) notice.textContent = '';
        this.updateDimensionUI(true);
        if (adjusted && notice) {
          const dimensions = this.state.dims.map((dimension, index) => [this.config.dimensionNames?.[index], dimension].filter(Boolean).join(': ')).join(' · ');
          notice.textContent = [dimensions, notice.textContent].filter(Boolean).join('. ');
        }
        return true;
      }

      // Kafle wymiarów: disabled dla nieistniejących/niedostępnych par + dynamiczne dopłaty
      updateDimensionUI(resetMattress) {
        for (let axis = 0; axis < this.dimCount; axis++) {
          const fieldset = this.groupFieldset(`dim:${axis}`);
          if (!fieldset) continue;
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const variant = this.dimensionVariant(axis, input.value);
            const usable = !!variant;
            const label = this.optionLabelEl(input);
            const priceEl = label && label.querySelector('.bed-option__price');
            input.disabled = !usable;
            if (label) label.classList.toggle('bed-option--disabled', !usable);
            if (!priceEl) return;
            priceEl.textContent = usable
              ? this.surchargeLabel(variant.priceCents - this.dimBasePrice(axis, variant.options))
              : this.getString('unavailable', '');
          });
        }
        this.renderAddonPriceLabels();
        this.updateMattressSize();
        if (resetMattress) this.resetMattress();
      }

      // Cena bazowa osi wg spec: wariant (default osi A, bieżąca oś B);
      // gdy taki wariant nie istnieje — najtańszy istniejący z tą samą osią B
      dimBasePrice(axis, testDims) {
        const baseDims = testDims.slice();
        baseDims[axis] = this.baseDims[axis];
        const base = this.findVariant(baseDims);
        if (base) return base.priceCents;
        let cheapest = null;
        this.config.variants.forEach((variant) => {
          for (let i = 0; i < this.dimCount; i++) {
            if (i !== axis && variant.options[i] !== testDims[i]) return;
          }
          if (cheapest === null || variant.priceCents < cheapest) cheapest = variant.priceCents;
        });
        return cheapest === null ? 0 : cheapest;
      }

      // Rozmiar materaca = szerokość × długość (opcja 2 × opcja 1)
      updateMattressSize() {
        const el = this.querySelector('[data-bed-mattress-size]');
        if (!el) return;
        const dims = this.state.dims;
        el.textContent =
          this.dimCount > 1 && dims[0] && dims[1] ? `${dims[1]} × ${dims[0]}` : dims.filter(Boolean).join(' × ');
      }

      // Zachowaj typ materaca, dopasowując jego wariant do nowych wymiarów.
      resetMattress() {
        const fieldset = this.groupFieldset('mattress');
        const group = this.config.groups.find(item => item.key === 'mattress');
        if (!fieldset || !group) return;
        const previous = this.state.picks.mattress;
        const usable = option => option.available !== false && (!!option.addonVariantId
          || (option.value === 'none' && !option.dimensionMatched && option.addonPriceCents === 0));
        const options = group.options || [];
        const selected = options.find(option => option.value === previous && usable(option))
          || options.find(option => option.default && usable(option));
        this.state.picks.mattress = selected?.value || null;
        fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          input.checked = input.value === this.state.picks.mattress;
        });
        const notice = this.querySelector('[data-bed-notice]');
        if (selected) {
          this.clearGroupError(fieldset);
          if (notice) notice.textContent = '';
        } else {
          if (notice) notice.textContent = this.getString('mattress_reset_notice', '');
          this.showGroupError(fieldset, this.getString('mattress_reset', ''));
        }
      }

      resolvePreviewMedia() {
        const fabric = this.state.picks.fabric;
        const dims = this.state.dims;
        const sizeToken = `size:${parseInt(dims[1], 10)}x${parseInt(dims[0], 10)}`;
        const fabricToken = `fabric:${fabric}`;
        const exact = (this.config.renderMedia || []).find(media => {
          const tokens = String(media.alt).split(/\s+/);
          return tokens.includes(sizeToken) && tokens.includes(fabricToken);
        });
        if (exact) return exact;
        const option = this.config.groups.find(group => group.key === 'fabric')?.options.find(option => option.value === fabric);
        return option?.packshot?.src ? option.packshot : null;
      }

      resolveProfessionalRender() {
        return this.resolvePreviewMedia()?.src || null;
      }

      updateMainImage() {
        this.updateRoomImage();
        const img = this.querySelector('.bed-configurator__media .bed-configurator__image');
        if (!img) return;
        if (!this._mainImageOriginal) {
          this._mainImageOriginal = { src: img.src, srcset: img.srcset, sizes: img.sizes, alt: img.alt, fullSrc: img.dataset?.fullSrc || img.src };
        }
        const media = this.resolvePreviewMedia();
        const target = media || this._mainImageOriginal;
        const fabric = this.config.groups.find(group => group.key === 'fabric')?.options.find(option => option.value === this.state.picks.fabric);
        const alt = media ? (media.alt || [this.config.productTitle, fabric?.label].filter(Boolean).join(' — ')) : this._mainImageOriginal.alt;
        const identity = [target.src, target.srcset || '', target.sizes || this._mainImageOriginal.sizes || ''].join('|');
        const status = this.querySelector('[data-bed-preview-status]');
        if (this._requestedRender === identity) {
          if (this._loadedRender === identity) img.alt = alt;
          return;
        }
        this._requestedRender = identity;
        const request = (this._renderRequest || 0) + 1;
        this._renderRequest = request;
        const targetSizes = target.sizes || this._mainImageOriginal.sizes || '';
        const absoluteSource = source => { try { return new URL(source, window.location.href).href; } catch { return source; } };
        // Reuse an already loaded SSR image only when the complete responsive
        // source contract matches. A new fabric, width set or sizes still loads.
        if (img.complete && img.naturalWidth > 0 && absoluteSource(img.src) === absoluteSource(target.src)
          && img.srcset === (target.srcset || '') && img.sizes === targetSizes) {
          this._loadedRender = identity;
          img.alt = alt;
          if (img.dataset) img.dataset.fullSrc = target.fullSrc || target.src;
          img.removeAttribute('aria-busy');
          if (status) status.textContent = media ? '' : this.getString('preview_generic', '');
          return;
        }
        img.setAttribute('aria-busy', 'true');
        if (status) status.textContent = this.getString('preview_loading', '');
        const pending = new Image();
        pending.onload = () => {
          if (request !== this._renderRequest) return;
          img.sizes = target.sizes || this._mainImageOriginal.sizes || '';
          img.srcset = target.srcset || '';
          img.src = target.src;
          if (img.dataset) img.dataset.fullSrc = target.fullSrc || target.src;
          this._loadedRender = identity;
          img.alt = alt;
          img.removeAttribute('aria-busy');
          if (status) status.textContent = media ? '' : this.getString('preview_generic', '');
        };
        pending.onerror = () => {
          if (request !== this._renderRequest) return;
          this._requestedRender = null;
          img.sizes = this._mainImageOriginal.sizes || "";
          img.srcset = this._mainImageOriginal.srcset;
          img.src = this._mainImageOriginal.src;
          img.alt = this._mainImageOriginal.alt;
          if (img.dataset) img.dataset.fullSrc = this._mainImageOriginal.fullSrc;
          img.removeAttribute('aria-busy');
          if (status) status.textContent = this.getString('preview_unavailable', '');
        };
        pending.sizes = target.sizes || this._mainImageOriginal.sizes || '';
        pending.srcset = target.srcset || '';
        pending.src = target.src;
      }

      updateRoomImage() {
        const options = this.config.groups.find(group => group.key === 'fabric')?.options || [];
        if (!options.some(option => option.roomImage?.src)) return;
        const img = this.querySelector('[data-bed-room-slide] img');
        if (!img) return;
        if (!this._roomImageOriginal) {
          this._roomImageOriginal = { src: img.src, srcset: img.srcset, sizes: img.sizes, alt: img.alt, fullSrc: img.dataset.fullSrc || img.src };
        }
        const fabric = options.find(option => option.value === this.state.picks.fabric);
        const target = fabric?.roomImage?.src ? fabric.roomImage : this._roomImageOriginal;
        const identity = [target.src, target.srcset || '', target.sizes || ''].join('|');
        if (this._requestedRoom === identity) return;
        this._requestedRoom = identity;
        const request = (this._roomRequest || 0) + 1;
        this._roomRequest = request;
        const apply = media => {
          img.sizes = media.sizes || '';
          img.srcset = media.srcset || '';
          img.src = media.src;
          img.alt = media.alt || [this.config.productTitle, fabric?.label, 'w pokoju'].filter(Boolean).join(' — ');
          img.dataset.fullSrc = media.fullSrc || media.src;
          img.removeAttribute('aria-busy');
        };
        if (img.complete && img.naturalWidth > 0 && new URL(img.src, window.location.href).href === new URL(target.src, window.location.href).href
          && img.srcset === (target.srcset || '') && img.sizes === (target.sizes || '')) {
          apply(target);
          return;
        }
        img.setAttribute('aria-busy', 'true');
        const pending = new Image();
        pending.onload = () => { if (request === this._roomRequest) apply(target); };
        pending.onerror = () => {
          if (request !== this._roomRequest) return;
          this._requestedRoom = null;
          apply(this._roomImageOriginal);
        };
        pending.sizes = target.sizes || '';
        pending.srcset = target.srcset || '';
        pending.src = target.src;
      }

      // Diagramy: schemat strony L/P + wartości w „Pełnych wymiarach"
      updateDiagrams() {
        const sideEl = this.querySelector('[data-bed-side-diagram]');
        if (sideEl) {
          const pick = this.state.picks.side;
          const option = this.config.groups.find(group => group.key === 'side')?.options.find(option => option.value === pick);
          const orientation = option?.orientation;
          const valid = orientation === 'left' || orientation === 'right';
          sideEl.dataset.side = valid ? orientation : '';
          const visual = sideEl.closest('[data-bed-side-visual]') || sideEl;
          visual.hidden = !valid;
        }
        const lenEl = this.querySelector('[data-bed-dims-length]');
        if (lenEl) lenEl.textContent = this.state.dims[0] || '—';
        const widEl = this.querySelector('[data-bed-dims-width]');
        if (widEl) widEl.textContent = this.state.dims[1] || '—';
        const hbEl = this.querySelector('[data-bed-dims-headboard]');
        if (hbEl) {
          const group = this.config.groups.find((g) => g.key === 'headboard');
          const pick = this.state.picks.headboard;
          const option = group && pick ? (group.options || []).find((o) => o.value === pick) : null;
          hbEl.textContent = option ? option.label : '—';
        }
      }

      // Zakres dat dostawy: dziś + min…max dni, locale dokumentu
      updateDelivery() {
        const el = this.querySelector('[data-bed-delivery]');
        const min = this.config.leadTimeMin;
        const max = this.config.leadTimeMax;
        if (!el || !Number.isFinite(min) || !Number.isFinite(max)) return;
        if (min === max) {
          el.textContent = this.getString('production_days_exact', '[days]').replace('[days]', min);
          return;
        }
        const from = new Date();
        from.setDate(from.getDate() + min);
        const to = new Date();
        to.setDate(to.getDate() + max);
        const options = { day: 'numeric', month: 'long' };
        if (from.getFullYear() !== to.getFullYear()) options.year = 'numeric';
        let formatter;
        try {
          formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'pl', options);
        } catch (error) {
          formatter = new Intl.DateTimeFormat('pl', options);
        }
        const range = `${formatter.format(from)} – ${formatter.format(to)}`;
        // Szablon z locales niesie niezależne placeholdery [from]/[to] — dowolny szyk
        // i separator tłumaczenia działają; fallback: sam zakres dat
        const template = this.getString('delivery_range', '');
        if (template.includes('[from]') && template.includes('[to]')) {
          el.textContent = template.replace('[from]', formatter.format(from)).replace('[to]', formatter.format(to));
        } else if (/\[range\]|\{\{\s*range\s*\}\}/.test(template)) {
          el.textContent = template.replace('[range]', range).replace(/\{\{\s*range\s*\}\}/, range);
        } else {
          el.textContent = range;
        }
      }

      // Suma = wariant + wybrane dopłaty (tylko podgląd)
      updateTotal() {
        if (!this.totalEl) return;
        const variant = this.findVariant(this.state.dims);
        const totalCents = variant ? variant.priceCents + this.addonsTotal() : 0;
        const compareCents = variant ? Math.max(variant.priceCents, variant.compareAtPriceCents || 0) + this.addonsCompareTotal() : 0;
        const onSale = !!variant && compareCents > totalCents;
        const total = variant ? this.formatMoney(totalCents) : this.getString('unavailable', '');
        this.querySelectorAll('[data-bed-compare-total]').forEach(el => {
          el.hidden = !onSale;
          el.textContent = onSale ? this.formatMoney(compareCents) : '';
        });
        this.querySelectorAll('[data-bed-sale-badge]').forEach(el => {
          el.hidden = !onSale;
          el.textContent = onSale ? `-${Math.round((compareCents - totalCents) * 100 / compareCents)}%` : '';
        });
        this.totalEl.textContent = total;
        this.querySelectorAll('[data-bed-live-total]').forEach(el => { el.textContent = total; });
        this.querySelectorAll('[data-bed-base-price]').forEach(el => { el.textContent = variant ? this.formatMoney(variant.priceCents) : '—'; });
        this.querySelectorAll('[data-bed-addons-price]').forEach(el => { el.textContent = this.formatMoney(this.addonsTotal()); });
      }

      addonsCompareTotal() {
        this.refreshDimensionAddons();
        return this.config.groups.reduce((total, group) => {
          const option = (group.options || []).find(o => o.value === this.state.picks[group.key]);
          return total + (option ? Math.max(option.addonPriceCents || 0, option.addonCompareAtPriceCents || 0) : 0);
        }, 0);
      }

      addonsTotal() {
        this.refreshDimensionAddons();
        let total = 0;
        this.config.groups.forEach((group) => {
          const pick = this.state.picks[group.key];
          if (!pick) return;
          const option = (group.options || []).find((o) => o.value === pick);
          if (option && option.addonPriceCents) total += option.addonPriceCents;
        });
        return total;
      }

      updateSummary() {
        this.querySelectorAll('[data-bed-summary-row]').forEach((row) => {
          const key = row.dataset.bedSummaryRow;
          let text = '—';
          if (key && key.startsWith('dim:')) {
            const axis = Number(key.slice(4));
            if (this.state.dims[axis]) text = this.state.dims[axis];
          } else {
            const group = this.config.groups.find((g) => g.key === key);
            const pick = this.state.picks[key];
            const option = group && pick ? (group.options || []).find((o) => o.value === pick) : null;
            if (option) text = option.label;
          }
          // Jeśli wiersz ma dedykowany element wartości (np. obok linku „Zmień"), pisz tylko tam
          const valueEl = row.querySelector('[data-bed-summary-value]') || row.querySelector('.bed-summary__value');
          (valueEl || row).textContent = text;
        });
      }

      /* ---------- walidacja ---------- */

      // Braki: wymagane grupy bez wyboru + niedostępna para wymiarów
      validationProblems() {
        this.refreshDimensionAddons();
        const problems = [];
        const requiredHint = this.getString('required_hint', '');
        this.querySelectorAll('[data-bed-group][data-bed-required]').forEach((fieldset) => {
          const key = fieldset.dataset.bedGroup;
          if (key.startsWith('dim:')) {
            const axis = Number(key.slice(4));
            if (!this.state.dims[axis]) problems.push({ fieldset, message: requiredHint });
          } else if (!this.state.picks[key]) {
            // A required group without options is incomplete merchant data,
            // not permission to purchase without the required selection.
            problems.push({ fieldset, message: requiredHint });
          }
        });
        this.config.groups.forEach(group => {
          const pick = this.state.picks[group.key];
          const option = (group.options || []).find(item => item.value === pick);
          if ((group.required && !option) || (option && (option.available === false || (option.addonPriceCents && !option.addonVariantId)))) {
            const fieldset = this.groupFieldset(group.key);
            if (fieldset) problems.push({ fieldset, message: this.getString('option_unavailable', '') });
          }
        });
        const variant = this.findVariant(this.state.dims);
        if (!variant || !variant.available) {
          const dimFieldset = this.querySelector('[data-bed-group^="dim:"]');
          if (dimFieldset && !problems.some((p) => p.fieldset === dimFieldset)) {
            problems.push({
              fieldset: dimFieldset,
              message: this.getString('unavailable', ''),
            });
          }
        }
        return problems;
      }

      // ATC przez aria-disabled (nie disabled), żeby klik pokazał braki
      updateAtcState() {
        if (!this.atcEl) return;
        this.atcEl.setAttribute('aria-disabled', this.validationProblems().length || this.busy ? 'true' : 'false');
      }

      showGroupError(fieldset, message) {
        const el = fieldset.querySelector('.bed-group__error');
        if (!el) return;
        el.textContent = message;
        el.classList.remove('hidden');
      }

      clearGroupError(fieldset) {
        const el = fieldset.querySelector('.bed-group__error');
        if (el) el.classList.add('hidden');
      }

      scrollToGroup(fieldset) {
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const step = fieldset.closest('details');
        if (step) step.open = true;
        fieldset.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        const input = fieldset.querySelector('input[type="radio"]:not([disabled])');
        if (input) input.focus({ preventScroll: true });
      }

      showStickyError(message) {
        const text =
          typeof message === 'string' && message.length
            ? message
            : this.getString('error_add', '');
        if (!this.stickyEl) {
          console.error('[bed-configurator]', text);
          return;
        }
        let el = this.stickyEl.querySelector('[data-bed-error]') || this.stickyEl.querySelector('.bed-sticky__error');
        if (!el) {
          el = document.createElement('div');
          el.className = 'bed-sticky__error';
          this.stickyEl.appendChild(el);
        }
        el.setAttribute('role', 'alert');
        el.textContent = text;
        el.classList.remove('hidden');
      }

      hideStickyError() {
        if (!this.stickyEl) return;
        const el = this.stickyEl.querySelector('[data-bed-error]') || this.stickyEl.querySelector('.bed-sticky__error');
        if (el) el.classList.add('hidden');
      }

      /* ---------- zdarzenia ---------- */

      onChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
        const fieldset = input.closest('[data-bed-group]');
        if (!fieldset) return;
        const key = fieldset.dataset.bedGroup;
        const shareOutput = this.querySelector('[data-bed-share-output]'); if (shareOutput) shareOutput.hidden = true;
        this.hideStickyError();
        this.clearGroupError(fieldset);
        if (key.startsWith('dim:')) {
          const axis = Number(key.slice(4));
          if (Number.isInteger(axis) && axis >= 0 && axis < this.state.dims.length) {
            this.selectDimension(axis, input.value);
          }
        } else {
          this.state.picks[key] = input.value;
          if (key === 'mattress') { const notice = this.querySelector('[data-bed-notice]'); if (notice) notice.textContent = ''; }
        }
        this.updateTotal();
        this.updateSummary();
        this.updateDiagrams();
        this.updateMainImage();
        this.updateAtcState();
        this.saveConfiguration();
        this.track('configuration_changed', { product_id: this.config.productId, model: this.config.model, dimensions: this.state.dims, picks: this.state.picks });
      }

      onAtcClick(event) {
        event.preventDefault();
        if (this.busy) return;
        if (this._cartResultUnknown) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }
        this.hideStickyError();
        const problems = this.validationProblems();
        if (problems.length) {
          problems.forEach((p) => this.showGroupError(p.fieldset, p.message));
          this.scrollToGroup(problems[0].fieldset);
          return;
        }
        this.submitConfiguration();
      }

      /* ---------- dodanie do koszyka ---------- */

      async submitConfiguration() {
        const variant = this.findVariant(this.state.dims);
        if (!variant || this.busy) return;
        const configId = `cfg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        let items;
        try { items = this.buildItems(variant, configId); } catch { this.showStickyError(this.getString('option_unavailable', '')); return; }
        this.busy = true;
        this.setLoading(true);
        const body = { items };
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const cartUrl = window.routes?.cart_url || '/cart';
        let submitted = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.sections = cart.getSectionsToRender().map(section => section.id);
            body.sections_url = window.location.pathname;
            cart.setActiveElement?.(document.activeElement);
          }
          const requestConfig = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'} };
          requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          requestConfig.body = JSON.stringify(body);
          requestConfig.signal = controller.signal;
          submitted = true;
          const http = await fetch(window.routes?.cart_add_url || '/cart/add', requestConfig);
          // A gateway/server timeout cannot prove that Shopify did not apply the POST.
          if (http.status >= 500 || http.status === 408) throw new Error('Unconfirmed cart mutation');
          const response = await http.json();
          if (response.status >= 500 || response.status === 408) throw new Error('Unconfirmed cart mutation');
          if (!http.ok || response.status) {
            this.showStickyError(typeof response.description === 'string' ? response.description : response.message);
            return;
          }
          this.track('configuration_added', {product_id:this.config.productId,variant_id:variant.id,value:(variant.priceCents + this.addonsTotal()) / 100});
          // An optional subscriber must not turn a successful order preparation into a retry prompt.
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, {source:'bed-configurator',cartData:response});
            }
          } catch (error) { console.error('[bed-configurator subscriber]',error); }
          if (!cart) { window.location = cartUrl; return; }
          try {
            const firstItem = Array.isArray(response.items) ? response.items[0] : null;
            const renderState = firstItem ? Object.assign({}, response, {key:firstItem.key}) : response;
            cart.classList.remove('is-empty');
            cart.renderContents(renderState);
          } catch (error) {
            // Items are already in Shopify; a plain cart page is a safe rendering fallback.
            window.location = cartUrl;
          }
        } catch (error) {
          if (submitted) {
            this._cartResultUnknown = true;
            this.showStickyError(this.getString('cart_result_unknown', ''));
          } else this.showStickyError(null);
        } finally {
          clearTimeout(timeout);
          this.busy = false;
          this.setLoading(false);
          this.updateAtcState();
          if (this._cartResultUnknown) {
            const label = this.atcEl?.querySelector('.bed-sticky__atc-label');
            if (label) label.textContent = this.getString('cart_inspect', '');
            this.atcEl?.setAttribute('aria-disabled','false');
          }
        }
      }

      // Linie koszyka: łóżko (properties PL + klucze techniczne) + produkty-dopłaty spięte _config_id
      buildItems(variant, configId) {
        this.refreshDimensionAddons();
        for (const group of this.config.groups) {
          const selected = group.options?.find(option => option.value === this.state.picks[group.key]);
          if (group.required && !selected) throw new Error('Required option is missing');
          if (selected?.dimensionMatched && !selected.available) throw new Error('No available add-on variant matches these dimensions');
        }
        const dims = this.state.dims;
        const dimNames = Array.isArray(this.config.dimensionNames) ? this.config.dimensionNames : [];
        const properties = {};
        for (let i = 0; i < this.dimCount; i++) {
          if (dims[i]) properties[dimNames[i] || this.getString('dimension_label', '').replace('[index]', i + 1)] = dims[i];
        }

        const configPicks = {};
        this.config.groups.forEach((group) => {
          const pick = this.state.picks[group.key];
          if (!pick) return;
          const option = (group.options || []).find((o) => o.value === pick);
          if (!option) return;
          properties[group.title || group.key] = option.label;
          configPicks[group.key] = option.productionNote
            ? { value: option.value, note: option.productionNote }
            : { value: option.value };
        });

        if (Number.isFinite(this.config.leadTimeMin) && Number.isFinite(this.config.leadTimeMax)) {
          properties._termin = this.config.leadTimeMin === this.config.leadTimeMax
            ? this.getString('lead_time_days_exact', '[days]').replace('[days]', this.config.leadTimeMin)
            : this.getString('lead_time_days', '').replace('[min]', this.config.leadTimeMin).replace('[max]', this.config.leadTimeMax);
        }
        properties._config_id = configId;
        const previewUrl = this.resolveProfessionalRender();
        if (previewUrl) properties._preview_url = previewUrl;
        properties._config_json = JSON.stringify({
          sku: variant.sku || '',
          dims: dims.slice(0, this.dimCount),
          picks: configPicks,
        });

        const items = [{ id: variant.id, quantity: 1, properties }];
        this.config.groups.forEach((group) => {
          const pick = this.state.picks[group.key];
          const option = pick ? (group.options || []).find((o) => o.value === pick) : null;
          if (option && option.addonVariantId) {
            items.push({
              id: option.addonVariantId,
              quantity: 1,
              properties: {
                [this.getString('addon_for_label', '').trim() || this.config.productTitle || group.title || group.key]: this.config.productTitle || '',
                [this.getString('addon_option_label', '').trim() || group.title || group.key]: option.label,
                _config_id: configId,
              },
            });
          }
        });
        return items;
      }

      setLoading(on) {
        if (!this.atcEl) return;
        if (on) {
          this._busyControls = [...this.querySelectorAll('.bed-option__input, .kd-config-next, .kd-config-navigation button, [data-bed-reset], [data-bed-share]')].filter(control => !control.disabled);
          this._busyControls.forEach(control => { control.disabled = true; });
        } else {
          (this._busyControls || []).forEach(control => { control.disabled = false; });
          this._busyControls = [];
        }
        const label = this.atcEl.querySelector('.bed-sticky__atc-label');
        if (label) {
          if (!this._atcLabel) this._atcLabel = label.textContent;
          label.textContent = on ? this.getString('adding', '') : this._atcLabel;
        }
        this.atcEl.setAttribute('aria-busy', String(on));
        this.atcEl.classList.toggle('loading', on);
        if (on) this.atcEl.setAttribute('aria-disabled', 'true');
        const spinner = this.atcEl.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
      }
    }
  );
}
